import { Component, OnInit, Input, ViewChild, Output, EventEmitter } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CallService } from 'src/app/services/call.service';
import { NotificationsService } from 'src/app/services/notifications.service';
import { Call } from 'src/app/models/call.model';
import { Prescription } from 'src/app/models/prescription.model';
import { Medicine } from 'src/app/models/medicine.model';
import { MedicinePrescription } from 'src/app/models/medicine-prescription.model';
import { autocompleteObjectValidator } from 'src/app/utils/autocomplete-validators.function';
import { MedicineFormComponent } from 'src/app/call-form/medicine-form/medicine-form.component';
import { Subject } from 'rxjs';
import * as _ from 'lodash';
import { CallStatus } from 'src/app/enums/call-status.enum';
import { DiagnosisService } from 'src/app/services/diagnosis.service';
import { SaleType } from 'src/app/enums/sale-type.enum';
import { MemedIntegrationService } from 'src/app/services/memed-integration.service';
import { PrescriptionGeneratorService } from 'src/app/enums/prescription-generator-service.enum';
import { PrescriptionGenerator, PrescriptionService } from 'src/app/services/prescription.service';
import { PrescriptionManagers } from 'src/app/enums/prescription-managers.enum';

const { MEMED } = PrescriptionGeneratorService;

@Component({
  selector: 'app-prescription',
  templateUrl: './prescription.component.html',
  styleUrls: ['./prescription.component.css'],
  standalone: false
})
export class PrescriptionComponent implements OnInit {
  @Input()
  call: Call;

  @Output()
  nextStepEvent = new EventEmitter<void>();

  @ViewChild(MedicineFormComponent) medicineFormComponent: MedicineFormComponent;
  PrescriptionGeneratorService = PrescriptionGeneratorService;

  isMedicineFormValid = false;
  hasSomeFilledFields = false;
  formGroup: FormGroup;
  resetSubject = new Subject<void>();
  prescriptionGenerator: PrescriptionGenerator | null;
  doctorChangesSendDuplicates = false;

  constructor(
    private callService: CallService,
    private notificationsService: NotificationsService,
    private formBuilder: FormBuilder,
    private diagnosisService: DiagnosisService,
    private memedIntegrationService: MemedIntegrationService,
    private prescriptionService: PrescriptionService
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.checkProlongedTreatment();
    this.prescriptionGenerator = this.prescriptionService.getGenerator(this.call);
  }

  buildForm(): void {
    this.formGroup = this.formBuilder.group({
      isPrescription: {
        value: this.call.isPrescription,
        disabled:
          this.call.status === CallStatus.ENDED_MANUAL ||
          (this.call.isPrescription && (this.call.originalCall || this.call.prescriptionManager === PrescriptionManagers.LAD))
      },
      prolongedTreatment: false,
      sendDuplicates: { value: false, disabled: false },
      prescriptions: this.formBuilder.array(this.fillMedicineForms())
    });
  }

  fillMedicineForms(): FormGroup[] {
    const formGroups: FormGroup[] = [];

    this.call?.prescriptions?.forEach((prescription: Prescription) => {
      const prescriptedMedicine = this.getPrescriptedMedicine(prescription);
      formGroups.push(this.setMedicineFormArray(prescriptedMedicine));
    });

    return formGroups;
  }

  getPrescriptedMedicine(prescription: Prescription): MedicinePrescription {
    return {
      medicine: prescription.medicines[0],
      quantity: prescription.medicines[0].quantity,
      instructions: prescription.medicines[0].instructions,
      status: prescription.status
    };
  }

  private setMedicineFormArray(medicinePrescripted: MedicinePrescription): FormGroup {
    const disabled = !!medicinePrescripted.status;
    return this.formBuilder.group({
      medicine: [{ value: medicinePrescripted.medicine, disabled }, [Validators.required, autocompleteObjectValidator()]],
      quantity: [{ value: medicinePrescripted.quantity, disabled }, [Validators.required, Validators.min(1), Validators.max(100)]],
      instructions: [{ value: medicinePrescripted.instructions || '', disabled }, [Validators.required]],
      status: medicinePrescripted.status
    });
  }

  async onClickPrescriptionGeneratorService(event: MouseEvent): Promise<void> {
    event?.preventDefault();
    if (this.prescriptionGenerator.url) {
      window.open(this.prescriptionGenerator.url, '_blank');
      return;
    }

    if (this.prescriptionGenerator.service === MEMED) {
      await this.memedIntegrationService.initialize(this.call.patient);
    }
  }

  onSelectMedicine(selectedMedicine: { medicine: Medicine; index: number }): void {
    const prescriptionFormGroup = this.prescriptions.controls.find(
      (prescriptionFormGroup: FormGroup, index) =>
        selectedMedicine.index != index && prescriptionFormGroup.get('medicine')?.value?.id === selectedMedicine.medicine.id
    );

    if (prescriptionFormGroup) {
      this.notificationsService.duplicatedMedicineMessage();
      this.medicineFormComponent.resetMedicineControl();
      this.prescriptions.controls[selectedMedicine.index]?.get('medicine').reset();
      let currentQuantity: number = prescriptionFormGroup.get('quantity').value;
      prescriptionFormGroup.get('quantity').setValue(++currentQuantity);
      this.checkProlongedTreatment();
    }

    this.checkSendDuplicates();
  }

  private checkSendDuplicates(): void {
    const duplicableSaleTypes = [SaleType.archivedPrescription, SaleType.officialPrescription];

    if (duplicableSaleTypes.includes(this.medicineFormComponent.medicine.value?.saleType)) {
      this.sendDuplicates.setValue(true);
      this.sendDuplicates.disable();
      return;
    }

    const sendDuplicates = this.prescriptions.controls.find(
      (prescriptionFormGroup: FormGroup) =>
        !prescriptionFormGroup.get('status')?.value && duplicableSaleTypes.includes(prescriptionFormGroup.get('medicine')?.value?.saleType)
    );
    if (sendDuplicates) {
      this.sendDuplicates.setValue(true);
      this.sendDuplicates.disable();
    } else {
      if (!this.doctorChangesSendDuplicates) this.sendDuplicates.setValue(false);
      this.sendDuplicates.enable();
    }
  }

  removeMedicine(index: number): void {
    this.prescriptions.removeAt(index);
    this.checkProlongedTreatment();
    this.checkSendDuplicates();
  }

  addMedicinePrescription(medicine: MedicinePrescription): void {
    this.prescriptions.push(this.setMedicineFormArray(medicine));
  }

  checkIsValidMedicineForm(isValid: boolean): void {
    this.isMedicineFormValid = isValid;
    const { medicine, instructions } = this.medicineFormComponent;
    this.hasSomeFilledFields = medicine?.value || instructions.value?.trim();
  }

  skipStep(): void {
    this.nextStepEvent.emit();
  }

  async next(): Promise<void> {
    const memedPrescriptionIds = this.memedIntegrationService.getPrescriptionIds();
    const prescriptionData = {
      call: this.call.id,
      isPrescription: this.isPrescription.value,
      ...(!this.prescriptionGenerator && { medicines: this.getPrescriptedMedicines() }),
      ...(!this.prescriptionGenerator && { prolongedTreatment: this.prolongedTreatment.value }),
      ...(!this.prescriptionGenerator && { sendDuplicates: this.sendDuplicates.value }),
      ...(this.prescriptionGenerator && { generatorService: this.prescriptionGenerator.service }),
      ...(this.prescriptionGenerator?.service === MEMED &&
        !_.isEmpty(memedPrescriptionIds) && { generatorPrescriptionIds: memedPrescriptionIds })
    };

    const areMedicinesValidated = await this.diagnosisService.validateRestrictedMedicines(prescriptionData.medicines);
    if (!areMedicinesValidated) return;

    // after validateRestrictedMedicines we don't need "medicine" anymore
    prescriptionData.medicines = prescriptionData.medicines?.map((elem) => {
      delete elem.medicine;
      return elem;
    });

    this.callService
      .createPrescription(prescriptionData)
      .then((createdPrescriptions) => {
        // Remove new prescriptions from array
        _.forEachRight(this.prescriptions.controls, (formGroup: FormGroup, index) => {
          if (!formGroup.controls.status?.value) this.prescriptions.removeAt(index);
        });

        createdPrescriptions?.forEach((prescription) => {
          this.call.prescriptions.push(prescription);
          // Push created prescriptions into array
          const prescriptedMedicine = this.getPrescriptedMedicine(prescription);
          this.prescriptions.push(this.setMedicineFormArray(prescriptedMedicine));
        });

        // Reset form
        this.resetSubject.next();

        this.memedIntegrationService.logoutFromMemed();

        // Go to next step
        this.nextStepEvent.emit();
      })
      .catch((err) => {
        const message = err.status === 401 ? err?.error?.message : null;
        this.notificationsService.openErrorDialog(
          {
            title: 'dialog_title_warning',
            message: message || 'prescription_error',
            actions: [
              {
                function: () => undefined,
                message: 'close'
              }
            ],
            error: err.status !== 401 ? err.error || err : null
          },
          false
        );
      });
  }

  getPrescriptedMedicines(): MedicinePrescription[] {
    let medicines: MedicinePrescription[] = [];

    this.prescriptions.value?.forEach((medicinePrescription: MedicinePrescription) => {
      if (!medicinePrescription.status) {
        medicines.push({
          id: medicinePrescription.medicine.id,
          medicine: medicinePrescription.medicine,
          instructions: medicinePrescription.instructions,
          quantity: medicinePrescription.quantity
        });
      }
    });

    const { medicine, quantity, instructions } = this.medicineFormComponent.formGroup.value;

    if (medicine?.id && quantity && instructions) medicines = [...medicines, { id: medicine.id, medicine, quantity, instructions }];

    return medicines;
  }

  checkProlongedTreatment(): void {
    const prolongedTreatment = this.prescriptions?.controls.some((formGroup: FormGroup) => formGroup.get('quantity').value >= 2);

    const { medicine, quantity } = this.medicineFormComponent || {};

    if ((medicine?.valid && medicine?.value && quantity?.value >= 2) || prolongedTreatment) {
      this.prolongedTreatment.disable();
      this.prolongedTreatment.setValue(true);
    } else {
      this.prolongedTreatment.enable();
      this.prolongedTreatment.setValue(false);
    }
  }

  get prescriptionGeneratorHasPrescriptions(): boolean {
    if (this.prescriptionGenerator?.service === MEMED) {
      return this.memedIntegrationService.getPrescriptionIds()?.length > 0;
    }
    return false;
  }

  get prescriptions(): FormArray {
    return this.formGroup.get('prescriptions') as FormArray;
  }
  get isPrescription(): FormControl<boolean> {
    return this.formGroup.get('isPrescription') as FormControl;
  }
  get prolongedTreatment(): FormControl<boolean> {
    return this.formGroup.get('prolongedTreatment') as FormControl;
  }
  get sendDuplicates(): FormControl<boolean> {
    return this.formGroup.get('sendDuplicates') as FormControl;
  }
}
